import type { OrganicAttribution } from '@tilly/shared';

const ATTRIBUTION_KEY = 'tilly_organic_attribution';
const INTERNAL_KEY = 'tilly_analytics_internal';
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

function clean(value: string | null, max = 256): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

export function isInternalAnalyticsBrowser(): boolean {
  try {
    return localStorage.getItem(INTERNAL_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setInternalAnalyticsBrowser(internal: boolean): void {
  if (internal) localStorage.setItem(INTERNAL_KEY, 'true');
  else localStorage.removeItem(INTERNAL_KEY);
}

export function readOrganicAttribution(now = Date.now()): OrganicAttribution | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const attribution = JSON.parse(raw) as OrganicAttribution;
    if (
      !attribution.id ||
      !attribution.tool_slug ||
      !attribution.landing_page.startsWith('/tools/') ||
      attribution.expires_at <= now
    ) {
      localStorage.removeItem(ATTRIBUTION_KEY);
      return null;
    }
    return attribution;
  } catch {
    localStorage.removeItem(ATTRIBUTION_KEY);
    return null;
  }
}

export function captureFirstToolAttribution(toolSlug: string): OrganicAttribution | null {
  if (localStorage.getItem('tilly_consent') !== 'granted') return null;
  const existing = readOrganicAttribution();
  if (existing) return existing;

  const url = new URL(window.location.href);
  const capturedAt = Date.now();
  const attribution: OrganicAttribution = {
    id: crypto.randomUUID(),
    tool_slug: toolSlug.slice(0, 128),
    landing_page: `/tools/${toolSlug}`,
    captured_at: capturedAt,
    expires_at: capturedAt + ATTRIBUTION_TTL_MS,
    referrer: clean(document.referrer, 2048),
    utm_source: clean(url.searchParams.get('utm_source')),
    utm_medium: clean(url.searchParams.get('utm_medium')),
    utm_campaign: clean(url.searchParams.get('utm_campaign')),
    utm_term: clean(url.searchParams.get('utm_term')),
    utm_content: clean(url.searchParams.get('utm_content')),
    gclid: clean(url.searchParams.get('gclid')),
    gbraid: clean(url.searchParams.get('gbraid')),
    wbraid: clean(url.searchParams.get('wbraid')),
    traffic_type: isInternalAnalyticsBrowser() ? 'internal' : 'external',
  };
  localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  return attribution;
}

export function organicAttributionParams(
  attribution = readOrganicAttribution()
): Record<string, string | number> {
  if (!attribution) return {};
  return {
    attribution_id: attribution.id,
    tool_slug: attribution.tool_slug,
    attribution_landing_page: attribution.landing_page,
    attribution_captured_at: attribution.captured_at,
    ...(attribution.utm_source ? { attribution_source: attribution.utm_source } : {}),
    ...(attribution.utm_medium ? { attribution_medium: attribution.utm_medium } : {}),
    ...(attribution.utm_campaign ? { attribution_campaign: attribution.utm_campaign } : {}),
    traffic_type: attribution.traffic_type ?? 'external',
  };
}

export function createAnalyticsEventId(): string {
  return crypto.randomUUID();
}
